// angular import
import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

// Project import
import { LoginGuard } from './guards/auth-login.guard';
import { AuthGuard } from './guards/auth.guard';
import { AdminComponent } from './theme/layouts/admin-layout/admin-layout.component';
import { GuestLayoutComponent } from './theme/layouts/guest-layout/guest-layout.component';

const routes: Routes = [
	{
		path: '',
		redirectTo: 'login',
		pathMatch: 'full'
	},
	{
		path: 'service/rating/:id',
		loadComponent: () =>
			import('./services/ui/service-rating/service-rating.component').then((c) => c.ServiceRatingComponent)
	},
	{
		path: 'terms-conditions',
		loadComponent: () => import('./terms-conditions/terms-conditions.component').then((c) => c.TermsConditionsComponent)
	},
	{
		path: 'terms-conditions-driver',
		loadComponent: () =>
			import('./terms-conditions-driver/terms-conditions-driver.component').then(
				(c) => c.TermsConditionsDriverComponent
			)
	},
	{
		path: 'data-deletion-policy',
		loadComponent: () =>
			import('./data-deletion-policy/data-deletion-policy.component').then((c) => c.DataDeletionPolicyComponent)
	},
	{
		path: 'privacy-policy',
		loadComponent: () => import('./privacy-policy/privacy-policy.component').then((c) => c.PrivacyPolicyComponent)
	},
	{
		path: '',
		component: AdminComponent,
		children: [
			{
				path: 'dashboard/home',
				canMatch: [AuthGuard],
				loadComponent: () => import('./dashboard/feature/home/home.component').then((c) => c.HomeComponent)
			},
			{
				path: 'drivers/map',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./dashboard/feature/drivers-map/drivers-map.component').then((c) => c.DriversMapComponent)
			},
			{
				path: 'driver/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-list/driver-list.component').then((c) => c.DriverListComponent)
			},
			{
				path: 'driver/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-create/driver-create.component').then((c) => c.DriverCreateComponent)
			},
			{
				path: 'driver/create/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/driver-create/driver-create.component').then((c) => c.DriverCreateComponent)
			},
			{
				path: 'driver/settle-trips/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/settlement/settlement.component').then((c) => c.SettlementComponent)
			},
			{
				path: 'driver/settlement',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/settlement/settlement.component').then((c) => c.SettlementComponent)
			},
			{
				path: 'services/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/services-list/services-list.component').then((c) => c.ServicesListComponent)
			},
			{
				path: 'passenger/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./passenger/feature/passenger-list/passenger-list.component').then((c) => c.PassengerListComponent)
			},
			{
				path: 'brands/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./brands/feature/brands-list/brands-list.component').then((c) => c.BrandsListComponent)
			},
			{
				path: 'category/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./category/feature/category-list/category-list.component').then((c) => c.CategoryListComponent)
			},
			{
				path: 'vehicle/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/vehicle-list/vehicle-list.component').then((c) => c.VehicleListComponent)
			},
			{
				path: 'admin/list',
				canMatch: [AuthGuard],
				loadComponent: () => import('./admin/feature/admin-list/admin-list.component').then((c) => c.AdminListComponent)
			},
			{
				path: 'coupon/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./coupon/feature/coupon-list/coupon-list.component').then((c) => c.CouponListComponent)
			},
			{
				path: 'legal-representatives-fuec-tourism/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import(
						'./legal-representatives-fuec-tourism/feature/legal-representative-fuec-tourism-list/legal-representative-fuec-tourism-list.component'
					).then((c) => c.LegalRepresentativeFuecTourismListComponent)
			},
			{
				path: 'parameters/list',
				redirectTo: 'helplines/list',
				pathMatch: 'full'
			},
			{
				path: 'helplines/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./helplines/feature/helplines-list/helplines-list.component').then((c) => c.HelplinesListComponent)
			},
			{
				path: 'create/vehicle',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/create-vehicle/create-vehicle.component').then((c) => c.CreateVehicleComponent)
			},
			{
				path: 'create/vehicle/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/feature/create-vehicle/create-vehicle.component').then((c) => c.CreateVehicleComponent)
			},
			{
				path: 'detail/vehicle/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./vehicles/ui/detail-vehicle/detail-vehicle.component').then((c) => c.DetailVehicleComponent)
			},
			{
				path: 'detail/driver/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./driver/feature/detail/driver-detail/driver-detail.component').then((c) => c.DriverDetailComponent)
			},
			{
				path: 'services/history',
				canMatch: [AuthGuard],
				redirectTo: 'services/history/finished',
				pathMatch: 'full'
			},
			{
				path: 'services/history/finished',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/history-services-list/history-services-list.component').then(
						(c) => c.HistoryServicesListComponent
					)
			},
			{
				path: 'services/history/expired-unmanaged',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/expired-unmanaged-services-list/expired-unmanaged-services-list.component').then(
						(c) => c.ExpiredUnmanagedServicesListComponent
					)
			},
			{
				path: 'rates/local',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/local-rates-v2-list/local-rates-v2-list.component').then(
						(c) => c.LocalRatesV2ListComponent
					)
			},
			{
				path: 'rates/local/config',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/local-config-editor/local-config-editor.component').then(
						(c) => c.LocalConfigEditorComponent
					)
			},
			{
				path: 'rates/foreigner',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/foreigner-tb-list/foreigner-tb-list.component').then((c) => c.ForeignerTbListComponent)
			},
			{
				path: 'rates/foreigner/v2',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/foreigner-rates-v2-list/foreigner-rates-v2-list.component').then(
						(c) => c.ForeignerRatesV2ListComponent
					)
			},
			{
				path: 'rates/foreigner/config',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/foreigner-config-editor/foreigner-config-editor.component').then(
						(c) => c.ForeignerConfigEditorComponent
					)
			},
			{
				path: 'rates/foreigner/discounts',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./rates/ui/foreigner-discount-list/foreigner-discount-list.component').then(
						(c) => c.ForeignerDiscountListComponent
					)
			},
			{
				path: 'pre-operational/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./pre-operational/feature/inspections-list/inspections-list.component').then(
						(c) => c.InspectionsListComponent
					)
			},
			{
				path: 'pre-operational/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./pre-operational/feature/inspections-detail/inspections-detail.component').then(
						(c) => c.InspectionsDetailComponent
					)
			},
			{
				path: 'fuec-config',
				canMatch: [AuthGuard],
				loadChildren: () => import('./fuec-config/feature/fuec-config.module').then((m) => m.FuecConfigModule)
			},
			{
				path: 'push-notifications/send',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./push-notifications/feature/push-notifications-send/push-notifications-send.component').then(
						(c) => c.PushNotificationsSendComponent
					)
			},
			{
				path: 'enterprise/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-list/enterprise-list.component').then(
						(c) => c.EnterpriseListComponent
					)
			},
			{
				path: 'enterprise/create',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-create/enterprise-create.component').then(
						(c) => c.EnterpriseCreateComponent
					)
			},
			{
				path: 'enterprise/create/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-create/enterprise-create.component').then(
						(c) => c.EnterpriseCreateComponent
					)
			},
			{
				path: 'enterprise/detail/:id',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/enterprise-detail/enterprise-detail.component').then(
						(c) => c.EnterpriseDetailComponent
					)
			},
			{
				path: 'enterprise/beneficiaries/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./enterprise/feature/beneficiaries-list/beneficiaries-list.component').then(
						(c) => c.BeneficiariesListComponent
					)
			},
			{
				path: 'profile',
				canMatch: [AuthGuard],
				loadComponent: () => import('./profile/feature/profile/profile.component').then((c) => c.ProfileComponent)
			},
			{
				path: 'notifications/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./notifications/feature/notifications-list/notifications-list.component').then(
						(c) => c.NotificationsListComponent
					)
			},
			{
				path: 'app-versions/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./app-versions/feature/app-versions-list/app-versions-list.component').then(
						(c) => c.AppVersionsListComponent
					)
			},
			{
				path: 'promotions/list',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./promotions/feature/promotions-list/promotions-list.component').then(
						(c) => c.PromotionsListComponent
					)
			},
			{
				path: 'flags/list',
				canMatch: [AuthGuard],
				loadComponent: () => import('./flags/feature/flags-list/flags-list.component').then((c) => c.FlagsListComponent)
			},
			{
				path: 'detail/service',
				canMatch: [AuthGuard],
				loadComponent: () =>
					import('./services/feature/detail-service-page/detail-service-page.component').then(
						(c) => c.DetailServicePageComponent
					)
			}
		]
	},
	{
		path: '',
		component: GuestLayoutComponent,
		children: [
			{
				path: 'login',
				canMatch: [LoginGuard],
				loadComponent: () =>
					import('./login/feature/login-admin/login-admin.component').then((c) => c.LoginAdminComponent)
			},
			{
				path: 'login/password',
				loadComponent: () =>
					import('./login/feature/recover-password/recover-password.component').then((c) => c.RecoverPasswordComponent)
			},
			{
				path: 'otp/validation',
				loadComponent: () =>
					import('./login/feature/otp-validation/otp-validation.component').then((c) => c.OtpValidationComponent)
			},
			{
				path: 'change/password',
				loadComponent: () =>
					import('./login/feature/change-password/change-password.component').then((c) => c.ChangePasswordComponent)
			}
		]
	},
	{
		path: '**',
		redirectTo: 'login-admin',
		pathMatch: 'full'
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule {}
